// @ts-nocheck
/**
 * Per-company actions drawer — right-side Sheet, matches the pattern used
 * by UserConsole on the Users page. Consolidates the "View Actions"
 * dropdown that used to live in the row into a proper drawer with
 * grouped action buttons (Manage / Credits / Lifecycle).
 *
 * The parent (CompanyListPage) still owns the API-calling handlers and
 * modals — this component just orchestrates the open state and dispatches
 * each button click to the callback the caller provided.
 */
import { Children, type ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Pencil,
  Phone,
  BadgePlus,
  BadgeMinus,
  Sparkles,
  Trash2,
  Building2,
  Info,
} from 'lucide-react';

interface Props {
  company: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showEdit: boolean;
  showAllocate: boolean;
  showCreditActions: boolean;
  showConvert: boolean;
  showDelete: boolean;
  isDemoRow: boolean;
  onEdit: () => void;
  onAllocate: () => void;
  onAddVoiceCredits: () => void;
  onRemoveVoiceCredits: () => void;
  onAddTtsCredits: () => void;
  onRemoveTtsCredits: () => void;
  onAddAiAgentCredits: () => void;
  onRemoveAiAgentCredits: () => void;
  onConvert: () => void;
  onDelete: () => void;
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  const hasChildren = Children.toArray(children).some(Boolean);
  return (
    <section className="rounded-md border bg-card">
      <header className="border-b px-4 py-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </header>
      {note}
      {hasChildren ? <div className="flex flex-col gap-2 p-3">{children}</div> : null}
    </section>
  );
}

function ActionButton({
  icon: Icon,
  label,
  description,
  destructive,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex items-start gap-3 rounded-md border border-transparent bg-transparent p-2 text-left transition-colors hover:border-border hover:bg-muted/60 ' +
        (destructive ? 'text-destructive hover:bg-destructive/10' : 'text-foreground')
      }
    >
      <span
        className={
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md ' +
          (destructive
            ? 'bg-destructive/10 text-destructive'
            : 'bg-primary/10 text-primary')
        }
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
    </button>
  );
}

export function CompanyConsole({
  company,
  open,
  onOpenChange,
  showEdit,
  showAllocate,
  showCreditActions,
  showConvert,
  showDelete,
  isDemoRow,
  onEdit,
  onAllocate,
  onAddVoiceCredits,
  onRemoveVoiceCredits,
  onAddTtsCredits,
  onRemoveTtsCredits,
  onAddAiAgentCredits,
  onRemoveAiAgentCredits,
  onConvert,
  onDelete,
}: Props) {
  // Close the drawer after firing an action — mirrors UserConsole's pattern
  // (`closeAnd(fn)`). The parent's modal or navigation then takes over.
  const runAndClose = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };

  const hasManage = showEdit || showAllocate;
  const hasCredits = showCreditActions;
  const hasLifecycle = showConvert || showDelete;
  const isPostpaid = company?.billing_type === 'postpaid';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="pr-8">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <SheetTitle className="truncate text-base">
                {company?.name || '—'}
              </SheetTitle>
              <SheetDescription className="truncate">
                {isDemoRow ? 'Demo company' : 'Tenant company'}
                {company?.company_code
                  ? ` · Code ${String(company.company_code).padStart(4, '0')}`
                  : ''}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
          {hasManage && (
            <Section title="Manage">
              {showEdit && (
                <ActionButton
                  icon={Pencil}
                  label="Edit Company"
                  description="Change company details, expiry, and licence."
                  onClick={runAndClose(onEdit)}
                />
              )}
              {showAllocate && (
                <ActionButton
                  icon={Phone}
                  label="Allocate Number"
                  description="Assign a DID or channel to this company."
                  onClick={runAndClose(onAllocate)}
                />
              )}
            </Section>
          )}

          {hasCredits && (
            <Section
              title="Credits"
              note={
                isPostpaid ? (
                  <div className="flex items-start gap-2 border-b bg-primary/5 px-4 py-2.5 text-xs text-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                    <span>This is a postpaid company. Voice, TTS, and AI credit changes are not available here.</span>
                  </div>
                ) : null
              }
            >
              {!isPostpaid && (
                <>
                  <ActionButton
                    icon={BadgePlus}
                    label="Add Voice Credits"
                    description="Top up voice-call balance."
                    onClick={runAndClose(onAddVoiceCredits)}
                  />
                  {!isDemoRow && (
                    <>
                      <ActionButton
                        icon={BadgeMinus}
                        label="Remove Voice Credits"
                        description="Deduct voice balance."
                        onClick={runAndClose(onRemoveVoiceCredits)}
                      />
                      <ActionButton
                        icon={BadgePlus}
                        label="Add TTS Credits"
                        description="Top up text-to-speech balance."
                        onClick={runAndClose(onAddTtsCredits)}
                      />
                      <ActionButton
                        icon={BadgeMinus}
                        label="Remove TTS Credits"
                        description="Deduct text-to-speech balance."
                        onClick={runAndClose(onRemoveTtsCredits)}
                      />
                      <ActionButton
                        icon={BadgePlus}
                        label="Add AI Agent Voice Credits"
                        description="Top up AI agent voice balance."
                        onClick={runAndClose(onAddAiAgentCredits)}
                      />
                      <ActionButton
                        icon={BadgeMinus}
                        label="Remove AI Agent Voice Credits"
                        description="Deduct AI agent voice balance."
                        onClick={runAndClose(onRemoveAiAgentCredits)}
                      />
                    </>
                  )}
                </>
              )}
            </Section>
          )}

          {hasLifecycle && (
            <Section title="Lifecycle">
              {showConvert && (
                <ActionButton
                  icon={Sparkles}
                  label="Convert to Actual Company"
                  description="Promote this demo to a paying tenant."
                  onClick={runAndClose(onConvert)}
                />
              )}
              {showDelete && (
                <ActionButton
                  icon={Trash2}
                  label="Delete Company"
                  description="Remove this company. Cannot be undone."
                  destructive
                  onClick={runAndClose(onDelete)}
                />
              )}
            </Section>
          )}

          {!hasManage && !hasCredits && !hasLifecycle && (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No actions available for this company under your current role.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
